// App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { API_BASE_URL } from './config';
import './App.css';
import icon from './assets/Logo - Side.png';
import Dashboard from './pages/dashboard/dashboard.js'
import Databases from './pages/databases/databases.js'
import TenantEditPage from './pages/tenants/TenantEditPage.js'
import PropertyEditPage from './pages/properties/PropertyEditPage.js'
import PropertyAddWizard from './pages/properties/PropertyAddWizard.js'
import StaffAddWizard from './pages/databases/StaffAddWizard.js'
import PricingEngine from './pages/pricing/PricingEngine.js'
import MaintenanceQueue from './pages/maintenance/MaintenanceQueue.js'
import LegalDashboard from './pages/legal/LegalDashboard.js'
import FinancialDashboard from './pages/financial/FinancialDashboard.js'
import TenantPortal from './pages/tenants/TenantPortal.js'
import CRM from './pages/crm/CRM.js'
import ApplicantDatabase from './pages/databases/ApplicantDatabase.js'
import ApplicantDashboard from './pages/applicant/ApplicantDashboard.js'
import RentalApplication from './pages/applicant/RentalApplication.js'
import ReportsDashboard from './pages/reports/ReportsDashboard.js'
import CheckoutPage from './pages/financial/CheckoutPage.js'
import CheckoutSuccessPage from './pages/financial/CheckoutSuccessPage.js'
import PropertyDetailView from './pages/properties/PropertyDetailView.js'
import TenantDetailView from './pages/tenants/TenantDetailView.js'
import PropertyListing from './pages/properties/PropertyListing.js'
import PropertyListingSwipe from './pages/properties/PropertyListingSwipe.js'
import PropertyDetailPage from './pages/properties/PropertyDetailPage.js'
import AdminDashboard from './pages/admin/AdminDashboard.js'
import ApplicantPortal from './pages/applicant/ApplicantPortal.js'
// SaaS Platform Pages
import LandingPage from './pages/saas/LandingPage.js'
import Signup from './pages/saas/Signup.js'
import VerifyPending from './pages/saas/VerifyPending.js'
import VerifyEmail from './pages/saas/VerifyEmail.js'
import Onboarding from './pages/saas/Onboarding.js'
import SuperAdminDashboard from './pages/saas/SuperAdminDashboard.js'


function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('manager'); // 'manager', 'tenant', or 'applicant'
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('authenticated') === 'true') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType === 'tenant') {
        navigate('/tenant-portal');
      } else if (storedUserType === 'applicant') {
        navigate('/applicant-portal');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, user_type: userType }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('authenticated', 'true');
        localStorage.setItem('username', data.username || username);
        localStorage.setItem('userType', userType);
        localStorage.setItem('lastActivity', Date.now().toString());
        
        // Store employee role and permissions for manager users
        if (userType === 'manager' && data.employee_role) {
          localStorage.setItem('employeeRole', data.employee_role);
          if (data.permissions) {
            localStorage.setItem('permissions', JSON.stringify(data.permissions));
          }
        }
        
        // Store additional tenant data if logging in as tenant
        if (userType === 'tenant' && data.tenant_id) {
          localStorage.setItem('tenantData', JSON.stringify({
            tenant_id: data.tenant_id,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            tenant_code: data.tenant_code,
            property_id: data.property_id,
            monthly_rent: data.monthly_rent
          }));
        }
        
        // Store applicant data if logging in as applicant
        if (userType === 'applicant' && data.applicant_id) {
          localStorage.setItem('applicantData', JSON.stringify({
            applicant_id: data.applicant_id,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            phone: data.phone,
            status: data.status,
            property_interest: data.property_interest
          }));
        }
        
        // Redirect based on user type
        if (userType === 'tenant') {
          navigate('/tenant-portal');
        } else if (userType === 'applicant') {
          navigate('/applicant-portal');
        } else {
          navigate('/dashboard');
        }
      } else {
        setMessage(data.message || 'Login unsuccessful');
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('Login unsuccessful');
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <img src={icon} alt="Logo" className="logo" />
        <form onSubmit={handleLogin}>
          <input
            type="text"
            placeholder="Username"
            className="input-field"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            className="input-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          
          {/* User Type Selection */}
          <div className="user-type-selection">
            <div className="user-type-option">
              <input
                type="radio"
                id="manager"
                name="userType"
                value="manager"
                checked={userType === 'manager'}
                onChange={(e) => setUserType(e.target.value)}
              />
              <label htmlFor="manager">👔 Manager</label>
            </div>
            <div className="user-type-option">
              <input
                type="radio"
                id="tenant"
                name="userType"
                value="tenant"
                checked={userType === 'tenant'}
                onChange={(e) => setUserType(e.target.value)}
              />
              <label htmlFor="tenant">🏠 Tenant</label>
            </div>
            <div className="user-type-option">
              <input
                type="radio"
                id="applicant"
                name="userType"
                value="applicant"
                checked={userType === 'applicant'}
                onChange={(e) => setUserType(e.target.value)}
              />
              <label htmlFor="applicant">📝 Applicant</label>
            </div>
          </div>
          
          <button type="submit" className="main-login-button">
            {userType === 'manager' ? 'Login as Manager' : userType === 'tenant' ? 'Login as Tenant' : 'Login as Applicant'}
          </button>
        </form>
        {message && <div className="message">{message}</div>}
        
        {/* Create Account Option */}
        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '14px' }}>
            Don't have an account?
          </p>
          <button 
            onClick={() => navigate('/signup')}
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '2px solid #e2e8f0',
              color: '#1e293b',
              padding: '12px 24px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              width: '100%'
            }}
            onMouseOver={(e) => {
              e.target.style.borderColor = '#1457f5';
              e.target.style.color = '#1457f5';
            }}
            onMouseOut={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.color = '#1e293b';
            }}
          >
            Create Account
          </button>
        </div>
        
        {/* Admin & utility links */}
        <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <button 
            onClick={() => navigate('/super-admin')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              padding: '8px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Admin Panel
          </button>
          <button 
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              padding: '8px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Clear session
          </button>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('authenticated') === 'true';
      const lastActivity = localStorage.getItem('lastActivity');
      
      if (auth && lastActivity) {
        const now = Date.now();
        const lastActivityTime = parseInt(lastActivity);
        const sessionTimeout = 30 * 60 * 1000; // 30 minutes
        
        if (now - lastActivityTime > sessionTimeout) {
          // Session expired, clear everything
          localStorage.removeItem('authenticated');
          localStorage.removeItem('username');
          localStorage.removeItem('lastActivity');
          setIsAuthenticated(false);
        } else {
          // Update last activity
          localStorage.setItem('lastActivity', now.toString());
          setIsAuthenticated(true);
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
    
    // Set up activity listeners
    const updateActivity = () => {
      if (localStorage.getItem('authenticated') === 'true') {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/" replace />;
}

function RoleProtectedRoute({ children, allowedUserType, requiredPage }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [hasPermission, setHasPermission] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('authenticated') === 'true';
      const storedUserType = localStorage.getItem('userType');
      const lastActivity = localStorage.getItem('lastActivity');
      
      if (auth && storedUserType && lastActivity) {
        const now = Date.now();
        const lastActivityTime = parseInt(lastActivity);
        const sessionTimeout = 30 * 60 * 1000; // 30 minutes
        
        if (now - lastActivityTime > sessionTimeout) {
          // Session expired, clear everything
          localStorage.removeItem('authenticated');
          localStorage.removeItem('username');
          localStorage.removeItem('userType');
          localStorage.removeItem('lastActivity');
          localStorage.removeItem('permissions');
          setIsAuthenticated(false);
          setUserType(null);
          setHasPermission(false);
        } else {
          // Update last activity
          localStorage.setItem('lastActivity', now.toString());
          setIsAuthenticated(true);
          setUserType(storedUserType);
          
          // Check page permissions if requiredPage is specified and user is manager
          if (requiredPage && storedUserType === 'manager') {
            const permissionsStr = localStorage.getItem('permissions');
            if (permissionsStr) {
              try {
                const permissions = JSON.parse(permissionsStr);
                const allowedPages = permissions.pages || [];
                // Check if the required page is in allowed pages, or if user has no restrictions (empty array means all access)
                setHasPermission(allowedPages.length === 0 || allowedPages.includes(requiredPage));
              } catch (e) {
                // If permissions parsing fails, allow access (fallback)
                setHasPermission(true);
              }
            } else {
              // No permissions set, allow access (fallback for existing users)
              setHasPermission(true);
            }
          } else {
            setHasPermission(true);
          }
        }
      } else {
        setIsAuthenticated(false);
        setUserType(null);
        setHasPermission(false);
      }
      setIsLoading(false);
    };

    checkAuth();
    
    // Set up activity listeners
    const updateActivity = () => {
      if (localStorage.getItem('authenticated') === 'true') {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [requiredPage]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Check if user is authenticated and has the correct role
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (userType !== allowedUserType) {
    // Redirect to appropriate route based on user type
    if (userType === 'tenant') {
      return <Navigate to="/tenant-portal" replace />;
    } else if (userType === 'applicant') {
      return <Navigate to="/applicant-portal" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check page permissions
  if (!hasPermission) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <Routes>
      {/* Main Routes */}
      <Route path="/" element={<Login />} />
      {/* SaaS Platform Routes */}
      <Route path="/pricing" element={<LandingPage />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-pending" element={<VerifyPending />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/super-admin" element={<SuperAdminDashboard />} />
      <Route
        path="/dashboard"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/dashboard">
            <Dashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/databases"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/databases">
            <Databases />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/crm">
            <CRM />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/applicant-database"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/applicant-database">
            <ApplicantDatabase />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/applicant/:applicantId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <ApplicantDashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenant/:tenantId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <TenantEditPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenant-edit/:tenantId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <TenantEditPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/property/:propertyId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <PropertyEditPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/properties/new-wizard"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <PropertyAddWizard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/staff/new-wizard"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <StaffAddWizard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/pricing-engine"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/pricing-engine">
            <PricingEngine />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/maintenance">
            <MaintenanceQueue />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/legal"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/legal">
            <LegalDashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/financial"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/financial">
            <FinancialDashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenant-portal"
        element={
          <RoleProtectedRoute allowedUserType="tenant">
            <TenantPortal />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/applicant-portal"
        element={
          <RoleProtectedRoute allowedUserType="applicant">
            <ApplicantPortal />
          </RoleProtectedRoute>
        }
      />
      <Route path="/apply" element={<RentalApplication />} />
      <Route path="/listings" element={<PropertyListing />} />
      <Route path="/listings-grid" element={<PropertyListing />} />
      <Route path="/listings/:propertyId" element={<PropertyDetailPage />} />
      <Route path="/checkout/:paymentId" element={<CheckoutPage />} />
      <Route path="/checkout/:paymentId/success" element={<CheckoutSuccessPage />} />
      <Route
        path="/portfolio/property/:propertyId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <PropertyDetailView />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/portfolio/tenant/:tenantId"
        element={
          <RoleProtectedRoute allowedUserType="manager">
            <TenantDetailView />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/admin">
            <AdminDashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/reporting"
        element={
          <RoleProtectedRoute allowedUserType="manager" requiredPage="/reporting">
            <ReportsDashboard />
          </RoleProtectedRoute>
        }
      />
    </Routes>
    
  );
}

export default App;